/**
 * JoinByCodeSheet — bottom sheet a signed-in user opens to ask to join a
 * team by typing the code their coach gave them. Submits to
 * POST /teams/join-by-code; the coach approves from /app/more.
 *
 * Used from both:
 *   - the in-app More page (signed-in users)
 *   - the post-signup landing for parents/players who picked
 *     "I have a team code" on the signup screen
 */
import { useState } from "react";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/chalkApi";
import { BottomSheet, ChalkActionButton } from "./primitives";

export default function JoinByCodeSheet({ isOpen, onDismiss, onJoined }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    const value = code.trim();
    if (!value) {
      toast.error("Type the code your coach gave you.");
      return;
    }
    setBusy(true);
    try {
      const data = await api.joinByCode(value);
      if (data.status === "already_member") {
        toast.success("You're already on that team.");
      } else if (data.status === "pending") {
        toast.success(data.team_name
          ? `Request sent to the coach of ${data.team_name}.`
          : "Request sent to the coach.");
      }
      onJoined && onJoined(data);
      setCode("");
      onDismiss && onDismiss();
    } catch (e) {
      toast.error(e.message || "Couldn't submit that code.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <BottomSheet isOpen={isOpen} title="Join a team" onDismiss={onDismiss}>
      <div data-testid="join-by-code-sheet" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ fontSize: 13, color: "var(--chalk-muted)", lineHeight: 1.5 }}>
          Type the join code your coach sent you. The coach gets a request
          and approves you onto the team.
        </div>
        <label style={{ display: "block" }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.10em", color: "var(--chalk-muted)", marginBottom: 6 }}>
            Team code
          </div>
          <input
            data-testid="join-by-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="CHLK-7421"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            autoFocus
            style={{
              width: "100%",
              fontFamily: "var(--font-display, 'Bebas Neue', sans-serif)",
              fontSize: 26, letterSpacing: "0.15em",
              border: "0.5px solid var(--chalk-border)",
              borderRadius: 10, padding: "12px 14px",
              background: "#FBF8F2", color: "var(--chalk-board)",
              textAlign: "center", outline: "none",
            }}
          />
        </label>
        <ChalkActionButton
          testId="join-by-code-submit"
          variant="primary"
          label={busy ? "Sending…" : "Send request"}
          icon={<KeyRound size={16} aria-hidden="true" />}
          loading={busy}
          onClick={submit}
        />
      </div>
    </BottomSheet>
  );
}
